
import React, { useState, useEffect } from "react";
import { NotificationProductData } from "@/types/notifications";
import { useProductSearch } from "@/hooks/useProductSearch";
import ProductList from "./product/ProductList";

interface ProductFieldsFormProps {
  products: NotificationProductData[];
  onProductsChange: (products: NotificationProductData[]) => void;
}

const ProductFieldsForm = ({ products, onProductsChange }: ProductFieldsFormProps) => {
  const { searchTerm, setSearchTerm, results, isLoading, searchProducts } = useProductSearch();
  const [activeField, setActiveField] = useState<{ index: number, field: string } | null>(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (activeField && (activeField.field === 'product_sku')) {
      const currentValue = products[activeField.index][activeField.field];
      if (typeof currentValue === 'string' && currentValue.length >= 2) {
        searchProducts(currentValue);
      }
    }
  }, [products, activeField]);

  const addNewProduct = () => {
    const newProduct: NotificationProductData = {
      product_name: '',
      product_eenheid: '',
      product_sku: '',
      product_bc: '',
      product_quantity: 1,
      product_color: ''
    };
    onProductsChange([...products, newProduct]);
  };

  const removeProduct = (index: number) => {
    const updatedProducts = [...products];
    updatedProducts.splice(index, 1);
    onProductsChange(updatedProducts);
  };

  const updateProductField = (index: number, field: keyof NotificationProductData, value: string | number) => {
    const updatedProducts = [...products];
    const currentProduct = updatedProducts[index];
    
    // Check if product has a KX SKU, which allows editing of protected fields
    const isKXProduct = currentProduct.product_sku?.toLowerCase().includes('kx');
    
    // These fields can only be filled via article number search
    // unless the product has a KX SKU
    const autoFillOnlyFields = ['product_name', 'product_eenheid', 'product_bc'];
    
    // If the field is one of the auto-fill only fields, and not a KX product, don't allow direct updates
    if (autoFillOnlyFields.includes(field) && !isKXProduct) return;

    // For product_sku field specifically
    if (field === 'product_sku') {
      // If clearing the SKU, also clear dependent fields
      if (typeof value === 'string' && value.trim() === '') {
        updatedProducts[index] = { 
          ...updatedProducts[index], 
          [field]: value,
          product_name: '',
          product_eenheid: '',
          product_bc: ''
        };
      } else {
        // Make sure we're assigning the correct type
        updatedProducts[index] = { 
          ...updatedProducts[index], 
          [field]: String(value)
        };
      }
    } else if (field === 'product_quantity') {
      // For quantity, ensure it's a number
      updatedProducts[index] = { 
        ...updatedProducts[index], 
        [field]: typeof value === 'number' ? value : parseInt(String(value)) || 1
      };
    } else {
      // For other fields, convert to string if necessary
      updatedProducts[index] = { 
        ...updatedProducts[index], 
        [field]: typeof value === 'string' ? value : String(value)
      };
    }
    
    onProductsChange(updatedProducts);
  };

  const handleInputFocus = (index: number, field: string) => {
    setActiveField({ index, field });
    if (field === 'product_sku') {
      setShowResults(true);
      const value = products[index][field];
      if (typeof value === 'string' && value.length >= 2) {
        searchProducts(value);
      }
    }
  };

  const handleInputBlur = () => {
    // Small delay to allow clicking on search results
    setTimeout(() => {
      setShowResults(false);
      setActiveField(null);
    }, 200);
  };

  const selectProduct = (product: any, index: number) => {
    const updatedProducts = [...products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      product_name: product.product_naam,
      product_eenheid: product.eenheid || '',
      product_sku: product.product_id,
      product_bc: product.bc
    };
    onProductsChange(updatedProducts);
    setShowResults(false);
  };

  return (
    <ProductList
      products={products}
      activeField={activeField}
      showResults={showResults}
      isLoading={isLoading}
      results={results}
      updateProductField={updateProductField}
      removeProduct={removeProduct}
      addNewProduct={addNewProduct}
      handleInputFocus={handleInputFocus}
      handleInputBlur={handleInputBlur}
      selectProduct={selectProduct}
    />
  );
};

export default ProductFieldsForm;
